/**
 * Promotions & messaging — pure form ↔ wire helpers.
 *
 * A campaign is one of four kinds: a recharge offer (a coin package at a promo
 * price), a login/bonus popup (extra coins on recharge), a call warning (a
 * popup the user must acknowledge before a call), or a safety banner.
 *
 * Money is handled the same way as coin packages: the operator types rupees,
 * the wire carries integer paise (`promoAmountPaise`, `minRechargePaise`).
 * Dates are typed in a native `datetime-local` input and sent as ISO strings.
 */
import { paiseToRupees, rupeesToPaise } from '../../lib/money';

export type PromoType = 'recharge_offer' | 'bonus_popup' | 'call_warning' | 'safety_banner';
/** `wallet` = highlighted package inside the Recharge Packages section only —
 *  no popup, no banner, no gift box; the user's window anchors at login. */
export type PromoPlacement = 'popup' | 'banner' | 'wallet';
export type PromoTrigger =
  | 'login'
  | 'home_open'
  | 'app_resume'
  | 'before_call'
  | 'call_connected'
  | 'low_balance';
export type PromoCompletionPolicy =
  | 'always'
  | 'until_first_recharge'
  | 'until_qualifying_recharge'
  | 'after_recharge'
  | 'limited';
export type PromoBonusType = 'percent' | 'fixed';

/** The create/update body, exactly as `POST/PATCH /admin/promos` takes it. */
export type PromoBannerTextPos = 'top' | 'center' | 'bottom';
export type PromoBannerTextAlign = 'left' | 'center' | 'right';

/**
 * Per-element design: what the popup draws at all, the colour of each piece of
 * text, and how big the image is.
 *
 * The point of the block: a popup shows what the admin typed and nothing else.
 * Price, struck-through "was" price, pack name and the placeholder artwork are
 * GENERATED copy, so they are off unless asked for; buttons with no label are
 * not drawn.
 */
export interface PromoDesign {
  showTitle: boolean;
  showMessage: boolean;
  showSubtitle: boolean;
  showPackage: boolean;
  showArt: boolean;
  showCta: boolean;
  showDismiss: boolean;
  titleColor: string | null;
  messageColor: string | null;
  subtitleColor: string | null;
  priceColor: string | null;
  ctaTextColor: string | null;
  dismissTextColor: string | null;
  imageHeight: number | null;
  imageWidthPercent: number | null;
  imageFit: 'contain' | 'cover';
}

/** Mirrors the API's PROMO_DESIGN_DEFAULTS. */
export const PROMO_DESIGN_DEFAULTS: PromoDesign = {
  showTitle: true,
  showMessage: true,
  showSubtitle: true,
  showPackage: false,
  showArt: false,
  showCta: true,
  showDismiss: true,
  titleColor: null,
  messageColor: null,
  subtitleColor: null,
  priceColor: null,
  ctaTextColor: null,
  dismissTextColor: null,
  imageHeight: null,
  imageWidthPercent: null,
  imageFit: 'contain',
};

/** The same design in form shape: a colour is '' while unset. */
export interface PromoDesignForm extends Omit<
  PromoDesign,
  'titleColor' | 'messageColor' | 'subtitleColor' | 'priceColor' | 'ctaTextColor' | 'dismissTextColor'
> {
  titleColor: string;
  messageColor: string;
  subtitleColor: string;
  priceColor: string;
  ctaTextColor: string;
  dismissTextColor: string;
}

export const BLANK_DESIGN_FORM: PromoDesignForm = {
  ...PROMO_DESIGN_DEFAULTS,
  titleColor: '',
  messageColor: '',
  subtitleColor: '',
  priceColor: '',
  ctaTextColor: '',
  dismissTextColor: '',
};

export interface PromoCampaignInput {
  name: string;
  type: PromoType;
  enabled: boolean;
  title: string;
  message: string;
  subtitle: string | null;
  ctaText: string | null;
  dismissText: string | null;
  showClose: boolean;
  imageKey: string | null;
  bannerTextPos: PromoBannerTextPos | null;
  bannerTextAlign: PromoBannerTextAlign | null;
  bannerOverlay: boolean;
  bgColor: string | null;
  textColor: string | null;
  accentColor: string | null;
  buttonColor: string | null;
  design: PromoDesign;
  placement: PromoPlacement;
  trigger: PromoTrigger;
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  /** Per-user countdown (seconds), anchored at the first impression. */
  countdownSec: number | null;
  /** Show the ticking timer to users? OFF by default — expiry still enforced. */
  showCountdown: boolean;
  packageId: string | null;
  /** The promo price actually charged; must be below the package price. */
  promoAmountPaise: number | null;
  lowBalanceThresholdCoins: number | null;
  bonusType: PromoBonusType | null;
  bonusValue: number | null;
  maxBonusCoins: number | null;
  minRechargePaise: number | null;
  eligiblePackageIds: string[] | null;
  completionPolicy: PromoCompletionPolicy;
  stopAfterAnyRecharge: boolean;
  requireAck: boolean;
  allowDismiss: boolean;
  showOncePerUser: boolean;
  maxImpressionsPerUser: number | null;
  maxPerDay: number | null;
  minGapMinutes: number | null;
  minAccountAgeDays: number | null;
  maxAccountAgeDays: number | null;
}

/** A campaign as `GET /admin/promos` returns it. */
export interface AdminPromoCampaign extends PromoCampaignInput {
  id: string;
  version: number;
  archivedAt: string | null;
  /** Signed URL of the uploaded image (derived from `imageKey`). */
  imageUrl: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `GET /admin/promos/:id/stats` — raw counters, never invented. */
export interface PromoStats {
  uniqueUsers: number;
  impressions: number;
  dismissals: number;
  ctaTaps: number;
  acknowledgements: number;
  conversions: number;
  convertedUsers: number;
  bonusCoinsCredited: number;
  capturedOrders: number;
  /** Paise, as a string (can exceed Number range on the wire). */
  capturedAmountPaise: string;
}

/** What the editor keeps in state: strings for text, ₹ for money inputs. */
export interface PromoForm {
  name: string;
  type: PromoType;
  enabled: boolean;
  title: string;
  message: string;
  subtitle: string;
  ctaText: string;
  dismissText: string;
  showClose: boolean;
  imageKey: string | null;
  /** Preview of `imageKey` (signed URL or a local object URL). */
  imageUrl: string | null;
  /** Banner layout — only meaningful while an image is set. */
  bannerTextPos: PromoBannerTextPos;
  bannerTextAlign: PromoBannerTextAlign;
  bannerOverlay: boolean;
  bgColor: string;
  textColor: string;
  accentColor: string;
  buttonColor: string;
  design: PromoDesignForm;
  placement: PromoPlacement;
  trigger: PromoTrigger;
  priority: number;
  /** `datetime-local` values ('' = not set). */
  startsAt: string;
  endsAt: string;
  countdownSec: number | null;
  showCountdown: boolean;
  packageId: string | null;
  /** Promo price in ₹; null = no discount. */
  promoAmountRupees: number | null;
  lowBalanceThresholdCoins: number | null;
  bonusType: PromoBonusType | null;
  bonusValue: number | null;
  maxBonusCoins: number | null;
  /** Minimum recharge in ₹ for the bonus; null = any. */
  minRechargeRupees: number | null;
  eligiblePackageIds: string[];
  completionPolicy: PromoCompletionPolicy;
  stopAfterAnyRecharge: boolean;
  requireAck: boolean;
  allowDismiss: boolean;
  showOncePerUser: boolean;
  maxImpressionsPerUser: number | null;
  maxPerDay: number | null;
  minGapMinutes: number | null;
  minAccountAgeDays: number | null;
  maxAccountAgeDays: number | null;
}

export const NAME_MIN = 2;
export const NAME_MAX = 80;
export const TITLE_MAX = 80;
export const MESSAGE_MAX = 400;
export const SUBTITLE_MAX = 200;
export const CTA_MAX = 32;
export const PRIORITY_MIN = -1000;
export const PRIORITY_MAX = 1000;
export const COUNTDOWN_MIN_SEC = 60;
export const COUNTDOWN_MAX_SEC = 2_592_000; // 30 days
export const PERCENT_BONUS_MAX = 500;
/** Promo price floor: the API refuses `promoAmountPaise` < 100 (₹1). */
export const MIN_PROMO_RUPEES = 1;

export const PROMO_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const PROMO_IMAGE_MAX_BYTES = 1024 * 1024; // 1 MiB (presign limit)

export const TYPE_LABELS: Record<PromoType, string> = {
  recharge_offer: 'Recharge offer',
  bonus_popup: 'Bonus popup',
  call_warning: 'Call warning',
  safety_banner: 'Safety banner',
};

export const PLACEMENT_LABELS: Record<PromoPlacement, string> = {
  popup: 'Popup',
  banner: 'Banner',
  wallet: 'Wallet highlight only',
};

export const TRIGGER_LABELS: Record<PromoTrigger, string> = {
  login: 'On login',
  home_open: 'On opening Home',
  app_resume: 'On returning to the app',
  before_call: 'Before a call',
  call_connected: 'When a call connects',
  // Fired when the app notices the balance dropped (never during a call).
  // Offers on this trigger show one by one, 2 s each — pair it with the
  // "Show only below N coins" threshold so the server decides what "low" is.
  low_balance: 'On low balance (sequential)',
};

export const POLICY_LABELS: Record<PromoCompletionPolicy, string> = {
  always: 'Always show',
  until_first_recharge: 'Until the first-ever recharge',
  until_qualifying_recharge: 'Until a qualifying recharge',
  after_recharge: 'Only after a recharge',
  limited: 'Limited run',
};

export const BONUS_TYPE_LABELS: Record<PromoBonusType, string> = {
  percent: 'Percent of recharge',
  fixed: 'Fixed coins',
};

/** Only recharge offers and bonus popups may carry package/bonus/threshold fields. */
export function isMoneyType(type: PromoType): boolean {
  return type === 'recharge_offer' || type === 'bonus_popup';
}

/** A blank form with sensible defaults for the given campaign type. */
/**
 * The "one-week login recharge discount" preset: a recharge offer that fires
 * on login, gives each user a PERSONAL 7-day window from the first time the
 * offer is served to them (repeat logins never restart it — the server
 * anchors the expiry once), and highlights the chosen package in the wallet
 * list with the badge. The admin still picks the package and the promo price;
 * every text below is a starting point, not a rule.
 */
export function loginOfferPreset(): PromoForm {
  return {
    ...emptyPromoForm('recharge_offer'),
    name: '7-day login offer',
    title: 'Special Welcome Offer',
    message: 'Recharge this week and pay less for the same coins.',
    subtitle: '7-Day Special Offer', // the wallet card's badge ribbon
    ctaText: 'Recharge Now',
    trigger: 'login',
    countdownSec: 7 * 86_400,
    // Spec: the offer lives INSIDE the Recharge Packages section — never a
    // popup. The login only starts the user's window server-side.
    placement: 'wallet',
  };
}

/**
 * Promo price (in ₹, two decimals) for "pct % off" the package's real price.
 * FLOORED to a whole paise value so the discount shown never overstates.
 * Null when the inputs can't make a valid price (the admin types on).
 */
export function promoPriceForPercent(amountPaise: string | number, pct: number): number | null {
  const base = Number(amountPaise);
  if (!Number.isFinite(base) || base <= 0) return null;
  if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) return null;
  const paise = Math.floor((base * (100 - pct)) / 100);
  if (paise < MIN_PROMO_RUPEES * 100) return null;
  return paiseToRupees(paise);
}

/** Promo price (₹) for "₹ off the package's real price". Same floor rules. */
export function promoPriceForFixedOff(amountPaise: string | number, offRupees: number): number | null {
  const base = Number(amountPaise);
  if (!Number.isFinite(base) || base <= 0) return null;
  if (!Number.isFinite(offRupees) || offRupees <= 0) return null;
  const paise = Math.floor(base - offRupees * 100);
  if (paise < MIN_PROMO_RUPEES * 100) return null;
  return paiseToRupees(paise);
}

export function emptyPromoForm(type: PromoType): PromoForm {
  const warning = type === 'call_warning';
  return {
    name: '',
    type,
    enabled: false,
    title: '',
    message: '',
    subtitle: '',
    ctaText: '',
    dismissText: '',
    showClose: !warning,
    imageKey: null,
    imageUrl: null,
    bannerTextPos: 'bottom',
    bannerTextAlign: 'center',
    bannerOverlay: true,
    bgColor: '',
    textColor: '',
    accentColor: '',
    buttonColor: '',
    design: { ...BLANK_DESIGN_FORM },
    placement: type === 'safety_banner' ? 'banner' : 'popup',
    trigger: warning ? 'before_call' : type === 'bonus_popup' ? 'login' : 'home_open',
    priority: 0,
    startsAt: '',
    endsAt: '',
    countdownSec: null,
    showCountdown: false,
    packageId: null,
    promoAmountRupees: null,
    lowBalanceThresholdCoins: null,
    bonusType: null,
    bonusValue: null,
    maxBonusCoins: null,
    minRechargeRupees: null,
    eligiblePackageIds: [],
    completionPolicy: 'always',
    stopAfterAnyRecharge: false,
    // The server rule is strict: requireAck ⇔ a popup that cannot be dismissed.
    requireAck: warning,
    allowDismiss: !warning,
    showOncePerUser: false,
    maxImpressionsPerUser: null,
    maxPerDay: null,
    minGapMinutes: null,
    minAccountAgeDays: null,
    maxAccountAgeDays: null,
  };
}

/** ISO → value for a `datetime-local` input, in the operator's timezone. */
export function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** `datetime-local` value → ISO for the wire; '' → null. */
export function localToIso(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formFromCampaign(c: AdminPromoCampaign): PromoForm {
  return {
    name: c.name,
    type: c.type,
    enabled: c.enabled,
    title: c.title,
    message: c.message,
    subtitle: c.subtitle ?? '',
    ctaText: c.ctaText ?? '',
    dismissText: c.dismissText ?? '',
    showClose: c.showClose,
    imageKey: c.imageKey,
    // Campaigns saved before the banner layout carry nulls; show the same
    // defaults the app falls back to.
    bannerTextPos: c.bannerTextPos ?? 'bottom',
    bannerTextAlign: c.bannerTextAlign ?? 'center',
    bannerOverlay: c.bannerOverlay ?? true,
    imageUrl: c.imageUrl,
    bgColor: c.bgColor ?? '',
    textColor: c.textColor ?? '',
    accentColor: c.accentColor ?? '',
    buttonColor: c.buttonColor ?? '',
    design: designToForm(c.design),
    placement: c.placement,
    trigger: c.trigger,
    priority: c.priority,
    startsAt: isoToLocal(c.startsAt),
    endsAt: isoToLocal(c.endsAt),
    countdownSec: c.countdownSec,
    showCountdown: c.showCountdown,
    packageId: c.packageId,
    promoAmountRupees: c.promoAmountPaise == null ? null : paiseToRupees(c.promoAmountPaise),
    lowBalanceThresholdCoins: c.lowBalanceThresholdCoins,
    bonusType: c.bonusType,
    bonusValue: c.bonusValue,
    maxBonusCoins: c.maxBonusCoins,
    minRechargeRupees: c.minRechargePaise == null ? null : paiseToRupees(c.minRechargePaise),
    eligiblePackageIds: c.eligiblePackageIds ?? [],
    completionPolicy: c.completionPolicy,
    stopAfterAnyRecharge: c.stopAfterAnyRecharge,
    requireAck: c.requireAck,
    allowDismiss: c.allowDismiss,
    showOncePerUser: c.showOncePerUser,
    maxImpressionsPerUser: c.maxImpressionsPerUser,
    maxPerDay: c.maxPerDay,
    minGapMinutes: c.minGapMinutes,
    minAccountAgeDays: c.minAccountAgeDays,
    maxAccountAgeDays: c.maxAccountAgeDays,
  };
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

function badColor(form: PromoForm): boolean {
  return [form.bgColor, form.textColor, form.accentColor, form.buttonColor].some(
    (c) => c.trim() !== '' && !HEX_COLOR.test(c.trim()),
  );
}

function badOptionalInt(v: number | null, min: number): boolean {
  return v != null && (!Number.isInteger(v) || v < min);
}

/**
 * Client-side mirror of the API's validation (the server re-checks every rule
 * and may add ones that need data we do not have, like the package price).
 * Returns the first problem, or null when the form can be saved.
 */
export function promoFormError(form: PromoForm): string | null {
  const name = form.name.trim();
  if (name.length < NAME_MIN || name.length > NAME_MAX)
    return `Name must be ${NAME_MIN}–${NAME_MAX} characters.`;
  // Title and message are optional: an image-only poster carries its copy
  // inside the artwork. Something must still be visible, though — a campaign
  // with no text and no image renders as an empty card.
  const title = form.title.trim();
  if (title.length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters.`;
  const message = form.message.trim();
  if (message.length > MESSAGE_MAX) return `Keep the message under ${MESSAGE_MAX} characters.`;
  if (title.length === 0 && message.length === 0 && form.imageUrl === null)
    return 'Add a title, a message, or an image — otherwise there is nothing to show.';
  if (form.subtitle.trim().length > SUBTITLE_MAX)
    return `Keep the subtitle under ${SUBTITLE_MAX} characters.`;
  if (form.ctaText.trim().length > CTA_MAX || form.dismissText.trim().length > CTA_MAX)
    return `Keep the button texts under ${CTA_MAX} characters.`;
  if (badColor(form)) return 'Colors must be #RRGGBB (e.g. #FB3C7F) or left empty.';
  if (!Number.isInteger(form.priority) || form.priority < PRIORITY_MIN || form.priority > PRIORITY_MAX)
    return `Priority must be a whole number between ${PRIORITY_MIN} and ${PRIORITY_MAX}.`;
  if (
    form.countdownSec != null &&
    (!Number.isInteger(form.countdownSec) ||
      form.countdownSec < COUNTDOWN_MIN_SEC ||
      form.countdownSec > COUNTDOWN_MAX_SEC)
  )
    return 'Countdown must be between 60 seconds and 30 days.';

  const startsAt = localToIso(form.startsAt);
  const endsAt = localToIso(form.endsAt);
  if (startsAt && endsAt && endsAt <= startsAt) return 'End must be after start.';

  if (
    form.type === 'call_warning' &&
    form.trigger !== 'before_call' &&
    form.trigger !== 'call_connected'
  )
    return 'A call warning must use the “Before a call” or “When a call connects” trigger.';

  if (!isMoneyType(form.type)) {
    const carries =
      form.packageId != null ||
      form.promoAmountRupees != null ||
      form.bonusType != null ||
      form.bonusValue != null ||
      form.maxBonusCoins != null ||
      form.minRechargeRupees != null ||
      form.eligiblePackageIds.length > 0 ||
      form.lowBalanceThresholdCoins != null;
    if (carries) return 'Only recharge offers and bonus popups can carry package or bonus fields.';
  }

  if (form.type === 'recharge_offer' && !form.packageId)
    return 'A recharge offer needs a coin package.';
  if (form.promoAmountRupees != null) {
    if (!form.packageId) return 'A promo price needs a coin package.';
    if (!(form.promoAmountRupees >= MIN_PROMO_RUPEES))
      return `Promo price must be at least ₹${MIN_PROMO_RUPEES} (and below the package price).`;
  }

  if ((form.bonusType == null) !== (form.bonusValue == null))
    return 'Bonus type and bonus value go together — set both or neither.';
  if (form.bonusValue != null && (!Number.isInteger(form.bonusValue) || form.bonusValue < 1))
    return 'Bonus value must be a whole number ≥ 1.';
  if (form.bonusType === 'percent' && form.bonusValue != null && form.bonusValue > PERCENT_BONUS_MAX)
    return `A percent bonus cannot exceed ${PERCENT_BONUS_MAX}%.`;
  if (badOptionalInt(form.maxBonusCoins, 1)) return 'Max bonus coins must be a whole number ≥ 1.';
  if (form.minRechargeRupees != null && !(form.minRechargeRupees >= 1))
    return 'Minimum recharge must be at least ₹1.';
  if (badOptionalInt(form.lowBalanceThresholdCoins, 0))
    return 'Low-balance threshold must be a whole number ≥ 0.';

  if (
    form.completionPolicy === 'limited' &&
    !endsAt &&
    form.maxImpressionsPerUser == null &&
    form.maxPerDay == null &&
    !form.showOncePerUser
  )
    return 'A limited campaign needs an end date, an impression cap, or “show once per user”.';

  if (form.requireAck && (form.placement !== 'popup' || form.allowDismiss))
    return 'Acknowledgement needs a popup that cannot be dismissed.';
  if (form.placement === 'wallet' && form.type !== 'recharge_offer')
    return 'Only a recharge offer can be a wallet highlight.';
  if (!form.requireAck && !form.allowDismiss)
    return 'A campaign without acknowledgement must stay dismissible.';

  if (badOptionalInt(form.maxImpressionsPerUser, 1))
    return 'Max impressions per user must be a whole number ≥ 1.';
  if (badOptionalInt(form.maxPerDay, 1)) return 'Max per day must be a whole number ≥ 1.';
  if (badOptionalInt(form.minGapMinutes, 0)) return 'Minimum gap must be a whole number ≥ 0 minutes.';
  if (badOptionalInt(form.minAccountAgeDays, 0) || badOptionalInt(form.maxAccountAgeDays, 0))
    return 'Account age limits must be whole numbers ≥ 0 days.';
  if (
    form.minAccountAgeDays != null &&
    form.maxAccountAgeDays != null &&
    form.maxAccountAgeDays < form.minAccountAgeDays
  )
    return 'Max account age cannot be below the min.';
  return null;
}

/** Request body for create / update: trimmed, '' → null, ₹ → paise. */
export function promoBody(form: PromoForm): PromoCampaignInput {
  const money = isMoneyType(form.type);
  const text = (s: string): string | null => s.trim() || null;
  return {
    name: form.name.trim(),
    type: form.type,
    enabled: form.enabled,
    title: form.title.trim(),
    message: form.message.trim(),
    subtitle: text(form.subtitle),
    ctaText: text(form.ctaText),
    dismissText: text(form.dismissText),
    showClose: form.showClose,
    imageKey: form.imageKey,
    bannerTextPos: form.bannerTextPos,
    bannerTextAlign: form.bannerTextAlign,
    bannerOverlay: form.bannerOverlay,
    bgColor: text(form.bgColor),
    textColor: text(form.textColor),
    accentColor: text(form.accentColor),
    buttonColor: text(form.buttonColor),
    design: designFromForm(form.design),
    placement: form.placement,
    trigger: form.trigger,
    priority: Math.trunc(form.priority),
    startsAt: localToIso(form.startsAt),
    endsAt: localToIso(form.endsAt),
    countdownSec: form.countdownSec,
    showCountdown: form.showCountdown,
    packageId: money ? form.packageId : null,
    promoAmountPaise:
      money && form.promoAmountRupees != null ? rupeesToPaise(form.promoAmountRupees) : null,
    lowBalanceThresholdCoins: money ? form.lowBalanceThresholdCoins : null,
    bonusType: money ? form.bonusType : null,
    bonusValue: money ? form.bonusValue : null,
    maxBonusCoins: money ? form.maxBonusCoins : null,
    minRechargePaise:
      money && form.minRechargeRupees != null ? rupeesToPaise(form.minRechargeRupees) : null,
    eligiblePackageIds: money && form.eligiblePackageIds.length > 0 ? form.eligiblePackageIds : null,
    completionPolicy: form.completionPolicy,
    stopAfterAnyRecharge: form.stopAfterAnyRecharge,
    requireAck: form.requireAck,
    allowDismiss: form.allowDismiss,
    showOncePerUser: form.showOncePerUser,
    maxImpressionsPerUser: form.maxImpressionsPerUser,
    maxPerDay: form.maxPerDay,
    minGapMinutes: form.minGapMinutes,
    minAccountAgeDays: form.minAccountAgeDays,
    maxAccountAgeDays: form.maxAccountAgeDays,
  };
}

/** Why this file cannot be uploaded (type / 1 MiB presign limit), or null. */
export function promoImageProblem(file: { type: string; size: number }): string | null {
  if (!PROMO_IMAGE_TYPES.includes(file.type)) return 'Use a PNG, JPG or WebP image.';
  if (file.size > PROMO_IMAGE_MAX_BYTES) return 'Keep the image under 1 MB.';
  return null;
}

/** 'Scheduled' before `startsAt`, 'Expired' after `endsAt`, else null. */
export function scheduleState(
  c: Pick<AdminPromoCampaign, 'startsAt' | 'endsAt'>,
  now: Date = new Date(),
): 'scheduled' | 'expired' | null {
  if (c.endsAt && new Date(c.endsAt).getTime() < now.getTime()) return 'expired';
  if (c.startsAt && new Date(c.startsAt).getTime() > now.getTime()) return 'scheduled';
  return null;
}

/** mm:ss / h:mm:ss / "Nd h:mm:ss" for the preview's countdown strip. */
export function formatCountdown(sec: number): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const days = Math.floor(sec / 86_400);
  const h = Math.floor((sec % 86_400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (days > 0) return `${days}d ${h}:${p(m)}:${p(s)}`;
  if (h > 0) return `${h}:${p(m)}:${p(s)}`;
  return `${p(m)}:${p(s)}`;
}

/** A stored design (possibly null, or missing keys from an older build) → the
 *  complete form shape, so every control has a defined value. */
export function designToForm(d: Partial<PromoDesign> | null | undefined): PromoDesignForm {
  return {
    ...BLANK_DESIGN_FORM,
    ...(d ?? {}),
    titleColor: d?.titleColor ?? '',
    messageColor: d?.messageColor ?? '',
    subtitleColor: d?.subtitleColor ?? '',
    priceColor: d?.priceColor ?? '',
    ctaTextColor: d?.ctaTextColor ?? '',
    dismissTextColor: d?.dismissTextColor ?? '',
  };
}

/** Form → API: '' becomes null, so "unset" stays unset rather than '#000000'. */
export function designFromForm(f: PromoDesignForm): PromoDesign {
  const hex = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    showTitle: f.showTitle,
    showMessage: f.showMessage,
    showSubtitle: f.showSubtitle,
    showPackage: f.showPackage,
    showArt: f.showArt,
    showCta: f.showCta,
    showDismiss: f.showDismiss,
    titleColor: hex(f.titleColor),
    messageColor: hex(f.messageColor),
    subtitleColor: hex(f.subtitleColor),
    priceColor: hex(f.priceColor),
    ctaTextColor: hex(f.ctaTextColor),
    dismissTextColor: hex(f.dismissTextColor),
    imageHeight: f.imageHeight,
    imageWidthPercent: f.imageWidthPercent,
    imageFit: f.imageFit,
  };
}
